/* eslint-disable no-use-before-define */
import { all, call, put, select, take, takeLatest } from '@redux-saga/core/effects'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import api from 'api/_legacy'
import { addDays, isWeekend } from 'date-fns'
import type { RootState } from 'state/store'

import { createLogoutExtraReducers } from '@/utilities/redux'

import {
  ApiAssociatedAccount,
  ApiBusinessUnit,
  ApiClient,
  ApiNote,
  ApiTradingAccount,
  HttpResponse
} from '../../../../../api/_legacy/generated-api'
import { TENANT_ID } from '../../../constants'
import { SavedState, TradeActivationStep } from '../../../types'
import { extractErrorMessagesFromServerResponse } from '../../../util/extract-error-messages-from-server-response'
import { getUtcDate, getUtcDateAsString } from '../../../util/get-utc-date'
import { Errors, revalidator, validator } from '../../../util/validator'
import { saveAccountForFirstTimeSaga, saveAccountSaga } from '../../00-builder/state/save-account'
import { termsRules } from './terms-rules'

const validate = validator(termsRules)
const revalidate = revalidator(termsRules)

export enum FundingSource {
  Unknown = 'Unknown',
  Cash = 'Cash',
  Transfer = 'Transfer',
  Switch = 'Switch'
}

export type UnlockedRunningTrade = {
  trade: ApiAssociatedAccount
  unlocked: boolean
}

export type UnlockedRunningTrades = UnlockedRunningTrade[]

export type TradeTerms = {
  accountName: string
  // isTransfer: boolean
  fullSwitch: boolean
  fundingSource: FundingSource
  investment: string
  isCollateralForLoan?: boolean
  requestedStartDate: string
  requiresSettlement: boolean
  switchInvestment: string
  switchTrade: ApiAssociatedAccount | null
  topup: string
  unlockedTrades: UnlockedRunningTrades
  withdraw: string
}

export type TermsStepState = {
  canProceed: boolean
  errors: Errors
  generalErrors: string[]
  minimumInvestmentAmount: number
  nextWorkingDay?: string
  terms: TradeTerms
}

const initialState: TermsStepState = {
  canProceed: true,
  errors: {},
  generalErrors: [],
  minimumInvestmentAmount: 0,
  terms: {
    accountName: '',
    fundingSource: FundingSource.Unknown,
    fullSwitch: true,
    investment: '',
    // isTransfer: undefined,
    isCollateralForLoan: false,
    requestedStartDate: '',
    switchInvestment: '',
    switchTrade: undefined,
    topup: '',
    unlockedTrades: [],
    withdraw: '',
    requiresSettlement: false
  }
}

type NoteClientBusinessUnit = {
  businessUnit: ApiBusinessUnit
  client: ApiClient
  note: ApiNote
}

export const termsSlice = createSlice({
  name: 'termsStep',
  initialState,
  reducers: {
    init: (state, action: PayloadAction<NoteClientBusinessUnit>) => {
      const { client, note } = action.payload
      const noteHasTrades = note.associatedTrades.length > 0

      if (noteHasTrades) {
        state.minimumInvestmentAmount = client.tradeBuildingSettings.minimumAdditionalTradeSize
      } else {
        state.minimumInvestmentAmount = client.tradeBuildingSettings.minimumNoteSize
      }

      let nextWorkingDay = getUtcDate(new Date())
      nextWorkingDay = addDays(nextWorkingDay, 1)
      while (isWeekend(nextWorkingDay)) {
        nextWorkingDay = addDays(nextWorkingDay, 1)
      }

      state.nextWorkingDay = nextWorkingDay.toUTCString()
    },
    reset: state => {
      state.canProceed = true
    },
    resetTerms: state => {
      state.terms = initialState.terms
    },
    setAccountName: (state, action: PayloadAction<string>) => {
      state.terms.accountName = action.payload
      revalidate(state, state.errors, 'accountName')
    },
    setInvestment: (state, action: PayloadAction<string>) => {
      state.terms.investment = action.payload
      revalidate(state, state.errors, 'investment')
    },
    setSwitchInvestment: (state, action: PayloadAction<string>) => {
      state.terms.switchInvestment = action.payload
      revalidate(state, state.errors, 'switchInvestment')
    },
    setSwitchTrade: (state, action: PayloadAction<string>) => {
      state.terms.switchTrade = state.terms.unlockedTrades.find(
        ({ trade }) => trade.accountId === action.payload
      )?.trade
      revalidate(state, state.errors, 'switchTrade')
    },
    setFundingSource: (state, action: PayloadAction<FundingSource>) => {
      state.terms.fundingSource = action.payload
      if (action.payload !== FundingSource.Cash) {
        state.terms.investment = ''
      }
      if (action.payload !== FundingSource.Switch) {
        state.terms.switchInvestment = ''
        state.terms.switchTrade = null
      }
      revalidate(state, state.errors, 'fundingSource')
    },
    setFullSwitch: (state, action: PayloadAction<boolean>) => {
      state.terms.fullSwitch = action.payload
    },
    setTopup: (state, action: PayloadAction<string>) => {
      state.terms.topup = action.payload
    },
    setWithdraw: (state, action: PayloadAction<string>) => {
      state.terms.withdraw = action.payload
    },
    setSettlementRequired: (state, action: PayloadAction<boolean>) => {
      state.terms.requiresSettlement = action.payload
    },
    setStartDate: (state, action: PayloadAction<string>) => {
      if (action.payload === '') {
        state.terms.requestedStartDate = ''
      } else if (action.payload !== undefined) {
        state.terms.requestedStartDate = getUtcDateAsString(new Date(action.payload))
        revalidate(state, state.errors, 'requestedStartDate')
      } else {
        state.terms.requestedStartDate = undefined
      }
    },
    setInvalid: (state, action: PayloadAction<Errors>) => {
      state.errors = action.payload
    },
    setServerSaveErrors: (state, action: PayloadAction<any>) => {
      state.errors = extractErrorMessagesFromServerResponse(action.payload)
    },
    setUnlockedRunningTrades: (state, action: PayloadAction<UnlockedRunningTrades>) => {
      state.terms.unlockedTrades = action.payload
    },
    requestMoveNext: () => {
      // dummy comment to keep linter happy
    },
    moveNext: () => {
      // dummy comment to keep linter happy
    },
    moveBack: () => {
      // dummy comment to keep linter happy
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    loadSwitchStateAction: (state, action: PayloadAction<string>) => {
      // dummy comment to keep linter happy
    }
  },
  extraReducers: builder => {
    const logoutReducers = createLogoutExtraReducers(initialState)
    Object.entries(logoutReducers).forEach(([key, reducer]) => {
      builder.addCase(key, reducer)
    })
  }
})

function* requestMoveNextSaga() {
  const state: RootState = yield select()
  const { terms } = state

  const result = validate(terms)

  if (!result.isValid) {
    yield put(actions.setInvalid(result.errors))
    return
  }
  if ((state.builder as any).accountId) {
    yield call(saveAccountSaga(put(actions.moveNext()), e => put(actions.setServerSaveErrors(e))))
  } else {
    yield call(
      saveAccountForFirstTimeSaga(put(actions.moveNext()), e => put(actions.setServerSaveErrors(e)))
    )
  }
}

function* getRunningTradeNamesSaga() {
  const state: RootState = yield select()
  if (state.terms.terms.fundingSource !== FundingSource.Switch) return
  const { noteId } = state.note.note
  const notes: HttpResponse<ApiNote[]> = yield call(api.tenant.noteDetail, TENANT_ID)

  if (!notes?.data || notes.data.length === 0) {
    yield put(actions.setUnlockedRunningTrades([]))
    return
  }
  const note = notes.data.find(note => note.noteId === noteId)
  const trades = note.associatedTrades.filter(
    associatedTrade => associatedTrade.accountStatus === 'Running' || []
  )

  const results: HttpResponse<ApiTradingAccount>[] = yield all(
    trades.map(trade => call(api.tenant.inprogressDetail2, trade.accountId, TENANT_ID))
  )

  const unlockedTrades = trades.map((trade, index) => ({
    trade,
    unlocked: results && results[index] && results[index]?.data.isLockedForUpdates === false
  }))
  yield put(actions.setUnlockedRunningTrades(unlockedTrades))
}

function* loadSwitchState(action: PayloadAction<string>) {
  yield take('termsStep/setUnlockedRunningTrades')
  yield put(actions.setSwitchTrade(action.payload))
}

export function* termsRootSaga() {
  yield all([
    takeLatest('termsStep/requestMoveNext', requestMoveNextSaga),
    takeLatest('termsStep/setFundingSource', getRunningTradeNamesSaga),
    takeLatest('termsStep/loadSwitchStateAction', loadSwitchState)
  ])
}

const loadState = (state: SavedState) => async dispatch => {
  const { currentAccountState, newAccount, pendingAccountState } = state.account
  const isNewAccount = newAccount === true
  const isLiveTrade = !isNewAccount
  const isActiveStatus = state.status === TradeActivationStep.active
  dispatch(termsSlice.actions.resetTerms())

  if (isLiveTrade) {
    if (pendingAccountState.deposit) {
      dispatch(termsSlice.actions.setTopup(pendingAccountState.deposit.toString()))
      dispatch(termsSlice.actions.setSettlementRequired(true))
    }
    if (pendingAccountState.withdrawal) {
      dispatch(termsSlice.actions.setWithdraw(pendingAccountState.withdrawal.toString()))
    }
    if (pendingAccountState.switchRequest) {
      const { isFullSwitch, switchAmount } = pendingAccountState.switchRequest
      dispatch(termsSlice.actions.setFundingSource(FundingSource.Switch))
      dispatch(termsSlice.actions.setFullSwitch(isFullSwitch))
      dispatch(termsSlice.actions.setSwitchInvestment(switchAmount?.toString() || ''))
      dispatch(
        termsSlice.actions.loadSwitchStateAction(
          pendingAccountState.switchRequest.originatingAccountId
        )
      )
    }
    // Ticket: 2311 . show initial investment with topup/withdraw for live trade in active status
    if (isActiveStatus) {
      dispatch(termsSlice.actions.setAccountName(currentAccountState.accountName))
      dispatch(termsSlice.actions.setInvestment(state.currentAccountValue.toString()))
      dispatch(termsSlice.actions.setStartDate(undefined))
    }
  } else {
    // 2337: for new accounts, we always use pending state
    const { accountName } = pendingAccountState
    const investment = pendingAccountState.deposit
    dispatch(termsSlice.actions.setAccountName(accountName))
    dispatch(termsSlice.actions.setInvestment(investment?.toString()))
    if (state.account.requestedStartDate) {
      dispatch(termsSlice.actions.setStartDate(state.account.requestedStartDate))
    }
    if (pendingAccountState.switchRequest) {
      dispatch(
        termsSlice.actions.loadSwitchStateAction(
          pendingAccountState.switchRequest.originatingAccountId
        )
      )
      const { isFullSwitch, switchAmount } = pendingAccountState.switchRequest
      dispatch(termsSlice.actions.setFundingSource(FundingSource.Switch))
      dispatch(termsSlice.actions.setFullSwitch(isFullSwitch))
      dispatch(termsSlice.actions.setSwitchInvestment(switchAmount?.toString() || ''))
    } else {
      const source = investment === undefined ? FundingSource.Transfer : FundingSource.Cash
      dispatch(termsSlice.actions.setFundingSource(source))
      dispatch(termsSlice.actions.setSettlementRequired(true))
    }
  }
}

export const actions = { ...termsSlice.actions, loadState }

export type TermsActions = typeof actions

export default termsSlice.reducer
